import { RecurrentFeathersModel } from './RecurrentFeathersModel'
import { DateRange, DateUtils } from '../services/date-utils.service'
import { RruleHelper } from '../features/recurrence/rruleHelper'
import { addMilliseconds, isSameMinute } from 'date-fns'
import { MsToHHMMPipe } from '../shared/pipes/timeconversion.pipe'

/**
 * Model representing an assistant's availability in calendar view
 * Handles recurring availabilities and time calculations
 */
export class CalendarAssistantAvailability extends RecurrentFeathersModel {
  static serviceName = 'features/assistant-availabilities-calendar/assistant-availabilities'

  // Core availability properties
  assistantId: number
  assistantFullName: string
  genderId: number
  plannedFrom: Date
  plannedTo: Date
  rrule: string
  recurrenceParentId: number
  recurrenceExceptionDate: Date
  recurrenceException: string
  note: string

  constructor(public id: number = null) {
    super(id)
  }

  /**
   * Generates the first occurrence of a recurring availability
   */
  generateFirstOccurrence() {
    const availability = CalendarAssistantAvailability.createFromProps(CalendarAssistantAvailability, this)
    availability.recurrenceParentId = null
    return availability
  }

  /**
   * Generates a new occurrence of a recurring availability
   * @param occurrenceStartDate Start date of the occurrence
   * @param occurrenceEndDate End date of the occurrence
   */
  generateOccurrence(occurrenceStartDate: Date, occurrenceEndDate: Date) {
    const availability = CalendarAssistantAvailability.createFromProps(CalendarAssistantAvailability, this)
    availability.id = null
    availability.plannedFrom = occurrenceStartDate
    availability.plannedTo = occurrenceEndDate
    availability.recurrenceParentId = this.id
    availability.recurrenceException = null
    availability.rrule = null
    availability.recurrenceExceptionDate = occurrenceStartDate
    return availability
  }

  /**
   * Expands recurring availabilities within a date range
   * @param aa Array of availabilities to expand
   * @param range Date range to filter by
   * @returns Filtered and expanded availabilities
   */
  static expandAvailabilitiesAndFilterByRange(aa: CalendarAssistantAvailability[], range: DateRange) {
    return this.expandAvailabilitiesByRrules(aa, range).filter((a) =>
      DateUtils.dateRangeOverlaps(range, a.getPlannedRange()),
    )
  }

  /**
   * Expands recurring availabilities using RRULE
   * @param availabilities Array of availabilities to expand
   * @param range Optional date range to limit expansion
   * @param expandParentsAsOccurrences Whether to include parent events
   */
  public static expandAvailabilitiesByRrules(
    availabilities: CalendarAssistantAvailability[],
    range?: DateRange,
    expandParentsAsOccurrences = false,
  ) {
    const expanded: CalendarAssistantAvailability[] = []

    availabilities.forEach((aa: CalendarAssistantAvailability) => {
      if (aa.rrule) {
        expanded.push(...this.expandAvailabilityByRrules(aa, range, expandParentsAsOccurrences))
      } else {
        expanded.push(aa)
      }
    })
    return expanded
  }

  /**
   * Expands a single availability using RRULE
   * @param parent Parent availability to expand
   * @param range Optional date range to limit expansion
   * @param expandParentAsOccurrence Whether to include parent event
   */
  private static expandAvailabilityByRrules(
    parent: CalendarAssistantAvailability,
    range?: DateRange,
    expandParentAsOccurrence = false,
  ) {
    const expanded: CalendarAssistantAvailability[] = []

    try {
      const occurenceStartDates: Date[] = RruleHelper.expandRecurrentFeathersModel(
        parent as RecurrentFeathersModel,
        range,
      )

      const parentStartDate = parent.getDateFrom()
      const parentDurationMs = parent.getDateTo().getTime() - parent.getDateFrom().getTime()

      occurenceStartDates.forEach((occurrenceStartDate) => {
        if (!expandParentAsOccurrence && isSameMinute(occurrenceStartDate, parentStartDate)) {
          expanded.push(parent.generateFirstOccurrence())
        } else {
          expanded.push(
            parent.generateOccurrence(occurrenceStartDate, addMilliseconds(occurrenceStartDate, parentDurationMs)),
          )
        }
      })

      return expanded
    } catch (e) {
      console.error(parent, e)
      return []
    }
  }

  /**
   * Calculates total duration of availabilities in HH:MM format
   * @param availabilities Array of availabilities to sum
   */
  static sumDurationHHMM(availabilities: CalendarAssistantAvailability[]): string {
    if (!availabilities) return '00:00'
    const durationMsTotal = availabilities.reduce((total, curr) => {
      return total + curr.getDurationMs()
    }, 0)
    return new MsToHHMMPipe().transform(durationMsTotal)
  }

  /**
   * Calculates total duration of availabilities in decimal hours
   * @param availabilities Array of availabilities to sum
   * @param decimalPlaces Number of decimal places to round to
   */
  static sumDurationHHDecimalMinutes(availabilities: CalendarAssistantAvailability[], decimalPlaces = 2) {
    if (!availabilities) return '00.00'
    const durationMsTotal = availabilities.reduce((total, curr) => {
      return total + curr.getDurationMs()
    }, 0)
    return DateUtils.millisecondsToHours(durationMsTotal).toFixed(2)
  }

  /**
   * Gets the duration of this availability in milliseconds
   */
  getDurationMs(): number {
    if (!this.plannedFrom || !this.plannedTo) return 0
    const from = new Date(this.plannedFrom)
    const to = new Date(this.plannedTo)
    return DateUtils.durationMs(from, to)
  }

  getDateFrom(): Date {
    return new Date(this.plannedFrom)
  }

  getDateTo(): Date {
    return new Date(this.plannedTo)
  }
}
